import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  getProjectDocuments, 
  uploadProjectDocument, 
  deleteProjectDocument 
} from '../api/projects-api';
import { ProjectDocument } from '../models/ProjectDocument';
import { useSnackbarContext } from '../contexts/useSnackBarContext';
import { useTranslation } from 'react-i18next';

/**
 * Hook to fetch project documents
 */
export const useProjectDocuments = (projectId: number) => {
  return useQuery({
    queryKey: ['projectDocuments', projectId],
    queryFn: () => getProjectDocuments(projectId),
    enabled: projectId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to upload a project document
 */
export const useUploadProjectDocument = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showSnackbar } = useSnackbarContext();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ 
      projectId, 
      document 
    }: { 
      projectId: number; 
      document: { name: string; content: string; type?: any } 
    }) => uploadProjectDocument(projectId, document),
    
    onSuccess: (data, variables) => {
      // Update the documents list
      queryClient.setQueryData<ProjectDocument[]>(
        ['projectDocuments', variables.projectId],
        (old = []) => [data, ...old]
      );
      
      showSuccess('project.documentUploadSuccess');
    },
    
    onError: (error: any) => {
      const message = error.response?.data?.message || t('project.documentUploadError');
      showSnackbar({ message, severity: 'error' });
    }
  });
};

/**
 * Hook to delete a project document
 */
export const useDeleteProjectDocument = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showSnackbar } = useSnackbarContext();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ 
      projectId, 
      documentId 
    }: { 
      projectId: number; 
      documentId: number 
    }) => deleteProjectDocument(projectId, documentId),
    
    onSuccess: (_, variables) => {
      // Remove the document from the list
      queryClient.setQueryData<ProjectDocument[]>(
        ['projectDocuments', variables.projectId],
        (old = []) => old.filter(doc => doc.id !== variables.documentId)
      );
      
      showSuccess('project.documentDeleteSuccess');
    },
    
    onError: (error: any) => {
      const message = error.response?.data?.message || t('project.documentDeleteError');
      showSnackbar({ message, severity: 'error' });
    }
  });
};